import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState } from '@/components/ListStates';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { dealsService, Deal, Pipeline, PipelineStage, ForecastRow, DealEmailThread, WinProbabilityResult, ActivitySummaryResult } from '@/services/deals.service';
import { contactsService } from '@/services/contacts.service';
import { settingsService } from '@/services/settings.service';
import { ActivityTimeline } from '@/components/ActivityTimeline';
import { NotesTab } from '@/components/NotesTab';
import { Plus, Trophy, GitBranch, X, Trash2, ClipboardList, StickyNote, Mail, MailOpen, MousePointerClick, TrendingUp, TrendingDown, Activity as ActivityIcon, Percent, Calendar } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useLabels, useCompanyCurrency, useCurrencyField } from '@/contexts/WorkspaceContext';

// Period filter options — keep the active Kanban from getting cluttered with
// stale won/lost deals from prior months. Default is 'active' which means
// "all in-flight deals + anything closed this month". The range is now applied
// at the BACKEND (via closeFrom/closeTo query params on /deals) so that at
// 10k+ deals the browser only ever loads the slice the user is looking at.
type DealsPeriod = 'active' | 'this_month' | 'last_month' | 'last_30_days' | 'last_90_days' | 'this_year' | 'last_year' | 'custom' | 'all';
const PERIOD_LABELS: Record<DealsPeriod, string> = {
  active: 'Active board',
  this_month: 'This month',
  last_month: 'Last month',
  last_30_days: 'Last 30 days',
  last_90_days: 'Last 90 days',
  this_year: 'This year',
  last_year: 'Last year',
  custom: 'Custom range…',
  all: 'All time',
};

/** Completes "…and none closed ___" for the board's empty state. */
const PERIOD_PHRASE: Record<DealsPeriod, string> = {
  active: 'this month',
  this_month: 'this month',
  last_month: 'last month',
  last_30_days: 'in the last 30 days',
  last_90_days: 'in the last 90 days',
  this_year: 'this year',
  last_year: 'last year',
  custom: 'in the selected date range',
  all: '',
};

/** Returns [start, end) for the given period (UTC, day-aligned). */
function periodRange(p: DealsPeriod, now = new Date()): { from?: Date; to?: Date } {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const startOfMonth = (yr: number, mo: number) => new Date(Date.UTC(yr, mo, 1));
  const startOfNextMonth = (yr: number, mo: number) => new Date(Date.UTC(yr, mo + 1, 1));
  const startOfYear = (yr: number) => new Date(Date.UTC(yr, 0, 1));
  const startOfTomorrowUtc = () => new Date(Date.UTC(y, m, now.getUTCDate() + 1));
  const daysAgoUtc = (n: number) => new Date(Date.UTC(y, m, now.getUTCDate() - n));
  switch (p) {
    case 'this_month':   return { from: startOfMonth(y, m), to: startOfNextMonth(y, m) };
    case 'last_month':   return { from: startOfMonth(y, m - 1), to: startOfMonth(y, m) };
    case 'last_30_days': return { from: daysAgoUtc(30), to: startOfTomorrowUtc() };
    case 'last_90_days': return { from: daysAgoUtc(90), to: startOfTomorrowUtc() };
    case 'this_year':    return { from: startOfYear(y), to: startOfYear(y + 1) };
    case 'last_year':    return { from: startOfYear(y - 1), to: startOfYear(y) };
    case 'active':       return { from: startOfMonth(y, m) }; // no upper bound — kept for label parity
    case 'custom':       return {}; // resolved from customFrom/customTo in caller
    case 'all':          return {};
  }
}

export default function DealsPage() {
  const companyCurrency = useCompanyCurrency();
  const t = useLabels();
  const { has } = useModuleAccess();
  const [searchParams] = useSearchParams();
  const [reportCurrency, setReportCurrency] = useState(companyCurrency);
  const [totalDeals, setTotalDeals] = useState(0);
  const [loadedPage, setLoadedPage] = useState(1);
  const [moreLoading, setMoreLoading] = useState(false);
  const [lossTarget, setLossTarget] = useState<{ deal: Deal; stageUid?: string } | null>(null);
  const [lossReason, setLossReason] = useState('');
  useEffect(() => { setReportCurrency(companyCurrency); }, [companyCurrency]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [forecast, setForecast] = useState<ForecastRow[]>([]);
  const [activePipelineUid, setActivePipelineUid] = useState<string>('');
  const [period, setPeriod] = useState<DealsPeriod>('active');
  // Custom-range state. Only used when period === 'custom'. Stored as
  // YYYY-MM-DD strings; converted to ISO at query time.
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [customOpen, setCustomOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Deal | null>(null);
  const [busy, setBusy] = useState(false);
  const [draggingDealUid, setDraggingDealUid] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Array<{ contactUid: string; firstName: string; lastName?: string; email?: string }>>([]);
  const [team, setTeam] = useState<Array<{ userUid: string; firstName: string; lastName?: string; email: string }>>([]);
  const [contactUid, setContactUid] = useState<string>('');
  const [ownerUserUid, setOwnerUserUid] = useState<string>('');

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useCurrencyField();
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [nextActivityAt, setNextActivityAt] = useState('');
  const [nextActivityType, setNextActivityType] = useState<'call' | 'meeting' | 'email' | 'task' | 'follow_up' | ''>('');
  const [nextActivityNote, setNextActivityNote] = useState('');

  // Resolve the current period to backend query params. `active` and `all`
  // pass no date bounds — the former relies on includeActive=true to fetch
  // live deals only, the latter fetches everything. Custom range uses the
  // explicit customFrom/customTo state.
  const resolveDateParams = (): { closeFrom?: string; closeTo?: string; includeActive: boolean } => {
    if (period === 'all') return { includeActive: true };
    if (period === 'active') return { includeActive: true, closeFrom: periodRange('active').from?.toISOString() };
    if (period === 'custom') {
      const out: { closeFrom?: string; closeTo?: string; includeActive: boolean } = { includeActive: true };
      if (customFrom) out.closeFrom = new Date(customFrom + 'T00:00:00.000Z').toISOString();
      if (customTo) {
        const t = new Date(customTo + 'T00:00:00.000Z');
        t.setUTCDate(t.getUTCDate() + 1); // exclusive upper bound
        out.closeTo = t.toISOString();
      }
      return out;
    }
    const r = periodRange(period);
    return {
      closeFrom: r.from?.toISOString(),
      closeTo: r.to?.toISOString(),
      includeActive: true,
    };
  };

  const refreshAll = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const ps = await dealsService.listPipelines();
      let pipelineList = ps;
      if (pipelineList.length === 0) {
        // Backend bootstraps a default pipeline lazily on first deal creation. For an
        // empty company we still want the UI to render — fall back to nothing.
        setPipelines([]);
        setStages([]);
        setDeals([]);
        setForecast([]);
        return;
      }
      setPipelines(pipelineList);
      const targetUid = activePipelineUid || pipelineList.find((p) => p.isDefault)?.pipelineUid || pipelineList[0].pipelineUid;
      setActivePipelineUid(targetUid);
      const dateParams = resolveDateParams();
      const [sts, list, fc] = await Promise.all([
        dealsService.listStages(targetUid),
        dealsService.list({ pipelineUid: targetUid, limit: 500, ...dateParams }),
        dealsService.forecast(targetUid),
      ]);
      setStages(sts);
      setDeals(list.items);
      setTotalDeals(list.total); setLoadedPage(1);
      setForecast(fc);
    } catch {
      // The interceptor already toasted; the page shows the error in place of the board.
      setLoadError(true);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshAll(); }, [activePipelineUid, period, customFrom, customTo]);

  // Load contacts + team once for the deal modal pickers.
  useEffect(() => {
    if (has('contacts')) contactsService.list({ limit: 200 })
      .then((r) => setContacts(r.items.map((c) => ({ contactUid: c.contactUid, firstName: c.firstName, lastName: c.lastName, email: c.email }))))
      .catch(() => { /* ignore — pickers fall back to empty */ });
    settingsService.listTeam()
      .then((r) => setTeam(r.map((u) => ({ userUid: u.userUid, firstName: u.firstName, lastName: u.lastName, email: u.email }))))
      .catch(() => { /* ignore */ });
  }, [has]);

  // Backend now applies the period filter, so `deals` is already the slice
  // for the chosen period. We still keep `filteredDeals` as a name for
  // continuity with the analytics strip + per-stage grouping below.
  const filteredDeals = deals;
  const hiddenCount = 0;

  const dealsByStage = useMemo(() => {
    const map = new Map<string, Deal[]>();
    for (const s of stages) map.set(s.stageUid, []);
    for (const d of filteredDeals) {
      if (!map.has(d.stageUid)) map.set(d.stageUid, []);
      map.get(d.stageUid)!.push(d);
    }
    return map;
  }, [filteredDeals, stages]);

  // Stage-keyed lookup for the analytics strip (won / lost / active terminals).
  const stageMap = useMemo(() => new Map(stages.map((s) => [s.stageUid, s])), [stages]);

  // Analytics rollups computed from the currently-loaded deals slice. Active /
  // won / lost partition is by stage.terminal — the canonical source.
  const stats = useMemo(() => {
    let wonCount = 0, wonValue = 0;
    let lostCount = 0, lostValue = 0;
    let activeCount = 0, activeValue = 0, activeWeighted = 0;
    for (const d of filteredDeals) {
      const stage = stageMap.get(d.stageUid);
      const amt = d.currency === reportCurrency ? d.amount ?? 0 : 0;
      const prob = (stage?.probability ?? 0) / 100;
      if (stage?.terminal === 'won') { wonCount++; wonValue += amt; }
      else if (stage?.terminal === 'lost') { lostCount++; lostValue += amt; }
      else { activeCount++; activeValue += amt; activeWeighted += amt * prob; }
    }
    const closed = wonCount + lostCount;
    const winRate = closed > 0 ? (wonCount / closed) * 100 : null;
    return { wonCount, wonValue, lostCount, lostValue, activeCount, activeValue, activeWeighted, winRate };
  }, [filteredDeals, stageMap, reportCurrency]);

  const totalValue = stats.activeValue + stats.wonValue + stats.lostValue;
  const weightedTotal = stats.activeWeighted;

  // Headline values read in the company's currency.
  const headlineCurrency = reportCurrency;
  const currencyOptions = Array.from(new Set([companyCurrency, ...forecast.map(row => row.currency).filter((value): value is string => !!value)])).sort();

  const openNewModal = () => {
    setEditing(null);
    setTitle(''); setDescription('');
    setAmount(''); setCurrency(companyCurrency); setExpectedCloseDate('');
    setNextActivityAt(''); setNextActivityType(''); setNextActivityNote('');
    setContactUid(''); setOwnerUserUid('');
    setModalOpen(true);
  };

  const openEditModal = (d: Deal) => {
    setEditing(d);
    setTitle(d.title);
    setDescription(d.description ?? '');
    setAmount(d.amount != null ? String(d.amount) : '');
    setCurrency(d.currency);
    setExpectedCloseDate(d.expectedCloseDate ? d.expectedCloseDate.slice(0, 10) : '');
    setNextActivityAt(d.nextActivityAt ? d.nextActivityAt.slice(0, 10) : '');
    setNextActivityType(d.nextActivityType ?? '');
    setNextActivityNote(d.nextActivityNote ?? '');
    setContactUid(d.contactUid ?? '');
    setOwnerUserUid(d.ownerUserUid ?? '');
    setModalOpen(true);
  };

  const save = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    if (amount && (!Number.isFinite(Number(amount)) || Number(amount) < 0)) { toast.error('Enter a valid non-negative amount'); return; }
    if (!/^[A-Z]{3}$/.test(currency)) { toast.error('Use a three-letter currency code'); return; }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        amount: amount ? parseFloat(amount) : undefined,
        currency,
        expectedCloseDate: expectedCloseDate || (editing ? '' : undefined),
        nextActivityAt: nextActivityAt || (editing ? '' : undefined),
        nextActivityType: nextActivityType || undefined,
        nextActivityNote: nextActivityNote.trim() || undefined,
        contactUid: contactUid || (editing ? '' : undefined),
        ownerUserUid: ownerUserUid || (editing ? '' : undefined),
      };
      if (editing) {
        await dealsService.update(editing.dealUid, payload);
        toast.success('Deal updated');
      } else {
        await dealsService.create({ ...payload, pipelineUid: activePipelineUid || undefined });
        toast.success('Deal created');
      }
      setModalOpen(false);
      await refreshAll();
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editing) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete "${editing.title}"?`)) return;
    setBusy(true);
    try {
      await dealsService.remove(editing.dealUid);
      toast.success('Deal removed');
      setModalOpen(false);
      await refreshAll();
    } finally { setBusy(false); }
  };

  const onDragStart = (uid: string) => setDraggingDealUid(uid);
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const onDrop = async (stageUid: string) => {
    if (!draggingDealUid || busy) return;
    const deal = deals.find((d) => d.dealUid === draggingDealUid);
    setDraggingDealUid(null);
    if (!deal || deal.stageUid === stageUid) return;
    if (stageMap.get(stageUid)?.terminal === 'lost') { setLossTarget({ deal, stageUid }); setLossReason(deal.lostReason ?? ''); return; }
    setBusy(true);
    try {
      const updated = await dealsService.moveStage(deal.dealUid, stageUid);
      setDeals(previous => previous.map(item => item.dealUid === updated.dealUid ? updated : item));
      await refreshAll();
    } catch { await refreshAll(); } finally { setBusy(false); }
  };

  const markWon = async () => {
    if (!editing) return;
    setBusy(true);
    try { await dealsService.win(editing.dealUid); toast.success('Marked won'); setModalOpen(false); await refreshAll(); }
    catch { /* interceptor */ } finally { setBusy(false); }
  };
  const markLost = async () => {
    if (!lossTarget || !lossReason.trim()) return;
    setBusy(true);
    try {
      if (lossTarget.stageUid) await dealsService.moveStage(lossTarget.deal.dealUid, lossTarget.stageUid, lossReason.trim());
      else await dealsService.lose(lossTarget.deal.dealUid, lossReason.trim());
      toast.success('Marked lost'); setLossTarget(null); setModalOpen(false); await refreshAll();
    } catch { /* interceptor */ } finally { setBusy(false); }
  };
  const loadMore = async () => {
    setMoreLoading(true);
    try {
      const result = await dealsService.list({ pipelineUid: activePipelineUid, limit: 500, page: loadedPage + 1, ...resolveDateParams() });
      setDeals(previous => [...previous, ...result.items.filter(row => !previous.some(item => item.dealUid === row.dealUid))]);
      setTotalDeals(result.total); setLoadedPage(value => value + 1);
    } catch { /* interceptor */ } finally { setMoreLoading(false); }
  };
  useEffect(() => {
    const dealUid = searchParams.get('deal');
    if (!dealUid) return;
    let cancelled = false;
    dealsService.get(dealUid).then(deal => { if (!cancelled) openEditModal(deal); }).catch(() => undefined);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <Topbar
        title={t('deals')}
        breadcrumb="Sales"
        description={
          loading
            ? 'Loading…'
            : loadError
              ? 'Deals could not be loaded'
              : `${deals.length} of ${totalDeals} deals loaded · ${reportCurrency} value ${totalValue.toLocaleString()} · Weighted open ${weightedTotal.toLocaleString()}`
        }
        right={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Select
                value={period}
                onChange={(e) => {
                  const next = e.target.value as DealsPeriod;
                  setPeriod(next);
                  if (next === 'custom') setCustomOpen(true);
                }}
                className="!h-9 !text-xs !min-w-[140px]"
                aria-label="Period"
                title="Period — narrows which deals load. Closed deals outside the range stay in the DB but aren't fetched."
              >
                {(Object.keys(PERIOD_LABELS) as DealsPeriod[]).map((p) => (
                  <option key={p} value={p}>
                    {p === 'custom' && customFrom && customTo
                      ? `Custom: ${customFrom} → ${customTo}`
                      : PERIOD_LABELS[p]}
                  </option>
                ))}
              </Select>
              {period === 'custom' && (
                <button
                  type="button"
                  onClick={() => setCustomOpen((v) => !v)}
                  className="absolute right-7 top-1/2 -translate-y-1/2 text-text-muted hover:text-primary"
                  title="Edit custom range"
                >
                  <Calendar size={12} />
                </button>
              )}
              {customOpen && period === 'custom' && (
                <div className="absolute right-0 top-full z-30 mt-1.5 w-72 rounded-xl border border-border bg-surface p-3 shadow-elevated">
                  <p className="mb-2 text-[11.5px] font-medium text-text-muted">Custom date range</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-1 block text-[10.5px] uppercase tracking-wider text-text-muted">From</label>
                      <input
                        type="date"
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                        className="w-full rounded-lg border border-border bg-surface-alt px-2 py-1.5 text-[12px] text-text-primary"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10.5px] uppercase tracking-wider text-text-muted">To</label>
                      <input
                        type="date"
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                        className="w-full rounded-lg border border-border bg-surface-alt px-2 py-1.5 text-[12px] text-text-primary"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-between gap-2">
                    <button
                      onClick={() => { setCustomFrom(''); setCustomTo(''); }}
                      className="text-[11.5px] text-text-muted hover:text-text-primary"
                    >
                      Clear
                    </button>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => { setCustomOpen(false); }}
                        className="rounded-lg border border-border px-2.5 py-1 text-[11.5px] text-text-muted hover:bg-surface-alt"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => setCustomOpen(false)}
                        disabled={!customFrom || !customTo}
                        className="rounded-lg bg-primary px-2.5 py-1 text-[11.5px] font-semibold text-white disabled:opacity-50"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {pipelines.length > 1 && (
              <Select
                value={activePipelineUid}
                onChange={(e) => setActivePipelineUid(e.target.value)}
                className="!h-9 !text-xs"
                aria-label="Pipeline"
              >
                {pipelines.map((p) => <option key={p.pipelineUid} value={p.pipelineUid}>{p.name}</option>)}
              </Select>
            )}
            <Button
              variant="primary"
              onClick={openNewModal}
              className="whitespace-nowrap min-w-[110px]"
              iconLeft={<Plus size={14} />}
            >
              New deal
            </Button>
          </div>
        }
      />

      <div className="px-7 py-7">
        {loadError && !loading && (
          <LoadErrorState what="your deals" onRetry={() => void refreshAll()} />
        )}

        {loading && pipelines.length === 0 && (
          <div className="flex gap-3 overflow-hidden">
            {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-[420px] w-[270px] shrink-0 rounded-2xl" />)}
          </div>
        )}

        {pipelines.length === 0 && !loading && !loadError && (
          <Card className="py-12 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary shadow-soft">
              <Trophy size={24} />
            </div>
            <p className="text-sm font-semibold text-text-primary">No pipeline yet</p>
            <p className="mx-auto mt-1 max-w-md text-xs text-text-muted">
              Track every customer conversation through to revenue. Create your first deal — we&apos;ll set
              up a default sales pipeline (New → Qualified → Proposal → Negotiation → Won / Lost) automatically. Customise stages
              later in Settings.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <Button onClick={openNewModal}><Plus size={14} /> Create your first deal</Button>
              <Link to="/settings/pipelines" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                Configure pipeline first →
              </Link>
            </div>
          </Card>
        )}

        {pipelines.length > 0 && !loadError && (
          <>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs text-text-muted">
              <span>Counts include all currencies. Values include only the selected currency; no conversion is applied.</span>
              <Select value={reportCurrency} onChange={event => setReportCurrency(event.target.value)} aria-label="Value currency" className="!w-auto !h-8">{currencyOptions.map(code => <option key={code}>{code}</option>)}</Select>
            </div>
            <DealsAnalyticsStrip
              stats={stats}
              currency={headlineCurrency}
              periodLabel={`${PERIOD_LABELS[period]} · loaded deals · values in ${reportCurrency}`}
              loading={loading}
            />

          {!loading && stages.length === 0 ? (
            <EmptyState
              className="mt-4"
              icon={<GitBranch size={28} />}
              title="This pipeline has no stages yet"
              description="Add stages such as New, Qualified and Won to this pipeline. Deals are tracked on the board stage by stage."
              action={
                <Link to="/settings/pipelines">
                  <Button iconLeft={<GitBranch size={14} />}>Set up stages</Button>
                </Link>
              }
            />
          ) : (
          <>
          {!loading && deals.length === 0 && (
            <EmptyState
              className="mt-4"
              icon={<Trophy size={28} />}
              title={period === 'all' ? 'No deals in this pipeline yet' : period === 'active' ? 'No open deals in this pipeline' : 'No deals in this period'}
              description={
                period === 'all'
                  ? 'Add your first deal, then drag it from stage to stage as the conversation moves towards a win.'
                  : `There are no open deals in this pipeline, and none closed ${PERIOD_PHRASE[period]}.`
              }
              action={
                <>
                  <Button iconLeft={<Plus size={14} />} onClick={openNewModal}>New deal</Button>
                  {period !== 'all' && <Button variant="secondary" onClick={() => setPeriod('all')}>Show all time</Button>}
                </>
              }
            />
          )}

          {/* The kanban is intentionally allowed to overflow horizontally —
              maxWidth:100% used to constrain the inner flex which prevented
              scrolling on phones, squashing columns. Remove the cap so that
              `overflow-x-auto` on the parent kicks in at narrow viewports. */}
          <div className="-mx-4 mt-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
            <div className={clsx('flex gap-3', deals.length > 0 ? 'min-h-[500px]' : 'min-h-[160px]')} style={{ width: `${stages.length * 280}px` }}>
              {stages.map((stage) => {
                const list = dealsByStage.get(stage.stageUid) ?? [];
                const stageValue = list.filter(deal => deal.currency === reportCurrency).reduce((sum, deal) => sum + (deal.amount ?? 0), 0);
                return (
                  <div
                    key={stage.stageUid}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(stage.stageUid)}
                    className="flex w-[270px] shrink-0 flex-col rounded-2xl border border-border bg-surface-alt/40"
                  >
                    <div className="flex items-center justify-between border-b border-border p-3">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: stage.color || '#a3a3a3' }} />
                        <span className="text-sm font-semibold text-text-primary">{stage.name}</span>
                        <span className="rounded-full bg-surface px-1.5 py-0.5 text-2xs text-text-muted">{list.length}</span>
                      </div>
                      <span className="text-2xs text-text-muted tabular-nums">
                        {reportCurrency} {stageValue.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex-1 space-y-2 overflow-y-auto p-2">
                      {list.length === 0 && (
                        <div className="rounded-lg border border-dashed border-border/60 px-2 py-6 text-center text-2xs text-text-muted">
                          Drop deals here
                        </div>
                      )}
                      {list.map((d) => (
                        <div
                          key={d.dealUid}
                          draggable={!busy}
                          onDragStart={() => onDragStart(d.dealUid)}
                          onClick={() => openEditModal(d)}
                          className={clsx(
                            'cursor-pointer rounded-lg border border-border bg-surface p-2.5 shadow-soft transition-shadow hover:shadow-elevated',
                            draggingDealUid === d.dealUid && 'opacity-50',
                          )}
                        >
                          <div className="text-[13px] font-medium text-text-primary truncate">{d.title}</div>
                          {d.amount != null && (
                            <div className="mt-1 text-xs font-semibold text-primary tabular-nums">
                              {d.amount.toLocaleString()} {d.currency}
                            </div>
                          )}
                          {d.expectedCloseDate && (
                            <div className="mt-1 text-2xs text-text-muted">
                              Close {new Date(d.expectedCloseDate).toLocaleDateString()}
                            </div>
                          )}
                          {d.nextActivityAt && (() => {
                            const due = new Date(d.nextActivityAt).getTime();
                            const overdue = due < Date.now();
                            return (
                              <div className={clsx(
                                'mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-2xs font-semibold',
                                overdue ? 'bg-danger-soft text-danger' : 'bg-info/10 text-info',
                              )}>
                                {overdue ? '⚠ Overdue' : '⏱ Due'}: {d.nextActivityType ?? 'activity'} {new Date(d.nextActivityAt).toLocaleDateString()}
                              </div>
                            );
                          })()}
                          {d.tags.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {d.tags.slice(0, 3).map((t) => (
                                <span key={t} className="rounded-full bg-surface-alt px-1.5 py-0.5 text-[9px] text-text-muted">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          </>
          )}
          {deals.length < totalDeals && <div className="mt-4 flex justify-center"><Button variant="secondary" onClick={loadMore} loading={moreLoading} disabled={loading}>Load more deals ({deals.length} of {totalDeals})</Button></div>}
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => !busy && setModalOpen(false)}
        title={editing ? 'Edit deal' : 'New deal'}
        widthClass={editing ? 'max-w-3xl' : 'max-w-lg'}
      >
        <div className="px-6 py-5">
          <FormField label="Title" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enterprise contract for Acme" />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Amount">
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </FormField>
            <FormField label="Currency">
              <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
            </FormField>
          </div>
          <FormField label="Expected close date">
            <Input type="date" value={expectedCloseDate} onChange={(e) => setExpectedCloseDate(e.target.value)} />
          </FormField>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Contact" hint={contacts.length ? undefined : 'No contacts yet — add some from the Contacts page'}>
              <Select value={contactUid} onChange={(e) => setContactUid(e.target.value)}>
                <option value="">— none —</option>
                {contacts.map((c) => (
                  <option key={c.contactUid} value={c.contactUid}>
                    {[c.firstName, c.lastName].filter(Boolean).join(' ')}{c.email ? ` (${c.email})` : ''}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Owner">
              <Select value={ownerUserUid} onChange={(e) => setOwnerUserUid(e.target.value)}>
                <option value="">— unassigned —</option>
                {team.map((u) => (
                  <option key={u.userUid} value={u.userUid}>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                  </option>
                ))}
              </Select>
            </FormField>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Next activity — when" hint="Pipedrive-style: what's next + by when">
              <Input type="date" value={nextActivityAt} onChange={(e) => setNextActivityAt(e.target.value)} />
            </FormField>
            <FormField label="Next activity — type">
              <Select
                value={nextActivityType}
                onChange={(e) => setNextActivityType(e.target.value as typeof nextActivityType)}
              >
                <option value="">— none —</option>
                <option value="call">Call</option>
                <option value="meeting">Meeting</option>
                <option value="email">Email</option>
                <option value="task">Task</option>
                <option value="follow_up">Follow up</option>
              </Select>
            </FormField>
          </div>
          {nextActivityType && (
            <FormField label="Next activity — note">
              <Input value={nextActivityNote} onChange={(e) => setNextActivityNote(e.target.value)} placeholder="Quick reminder for yourself…" maxLength={280} />
            </FormField>
          )}
          <FormField label="Description">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormField>

          {editing?.lostReason && <p className="mt-3 rounded-lg bg-surface-alt p-3 text-sm text-text-secondary"><strong>Lost reason:</strong> {editing.lostReason}</p>}
          {editing && <div className="mt-3 flex gap-4 text-sm text-primary">{editing.contactUid && has('contacts') && <Link to={`/contacts/${editing.contactUid}`}>Customer profile ↗</Link>}{editing.leadUid && has('leads') && <Link to={`/leads/${editing.leadUid}`}>Linked lead ↗</Link>}</div>}
          {editing && (
            <div className="mt-6 grid grid-cols-1 gap-5 border-t border-border pt-5 lg:grid-cols-2">
              <div className="lg:col-span-2">
                {has('ai_copilot') && <DealAiPanel dealUid={editing.dealUid} />}
              </div>
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  <ClipboardList size={12} /> Activity
                </div>
                <div className="max-h-[360px] overflow-y-auto pr-1">
                  {has('calendar') && <ActivityTimeline entityType="deal" entityUid={editing.dealUid} limit={30} />}
                </div>
              </div>
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  <StickyNote size={12} /> Notes
                </div>
                <div className="max-h-[360px] overflow-y-auto pr-1">
                  <NotesTab entityType="deal" entityUid={editing.dealUid} />
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  <Mail size={12} /> Emails
                </div>
                <DealEmailThreadPanel dealUid={editing.dealUid} />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          {editing ? (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={remove} disabled={busy}><Trash2 size={14} /> Delete</Button>
              <Button variant="ghost" onClick={markWon} disabled={busy}><Trophy size={14} /> Won</Button>
              <Button variant="ghost" onClick={() => { setLossTarget({ deal: editing }); setLossReason(editing.lostReason ?? ''); }} disabled={busy}><X size={14} /> Lost</Button>
            </div>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={busy} disabled={busy || !title.trim()}>
              {editing ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal open={!!lossTarget} onClose={() => !busy && setLossTarget(null)} title="Mark deal as lost" widthClass="max-w-lg">
        <div className="px-6 py-5"><p className="mb-4 text-sm text-text-secondary">Record why “{lossTarget?.deal.title}” was lost.</p><FormField label="Lost reason" required><Textarea value={lossReason} onChange={event => setLossReason(event.target.value)} rows={3} maxLength={1000} placeholder="Budget, timing, competitor or another reason…" /></FormField></div>
        <div className="flex justify-end gap-2 border-t border-border px-6 py-4"><Button variant="secondary" disabled={busy} onClick={() => setLossTarget(null)}>Cancel</Button><Button disabled={!lossReason.trim()} loading={busy} onClick={markLost}>Mark lost</Button></div>
      </Modal>
    </>
  );
}

/**
 * Per-deal email thread — outbound (full HTML, with open/click tracking
 * status) interleaved with inbound snippets from `email_received` lead-events.
 */
function DealEmailThreadPanel({ dealUid }: { dealUid: string }) {
  const [thread, setThread] = useState<DealEmailThread | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dealsService.emails(dealUid)
      .then(setThread)
      .catch(() => setThread({ outbound: [], inbound: [] }))
      .finally(() => setLoading(false));
  }, [dealUid]);

  if (loading) return <div className="text-xs text-text-muted">Loading…</div>;
  if (!thread) return null;

  const merged = [
    ...thread.outbound.map((m) => ({ ...m, t: m.sentAt ?? m.createdAt })),
    ...thread.inbound.map((m) => ({ ...m, t: m.createdAt })),
  ].sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());

  if (merged.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-4 py-6 text-center text-xs text-text-muted">
        No emails for this deal yet. Sent emails to the linked contact and inbound replies will appear here.
      </div>
    );
  }

  return (
    <div className="max-h-[420px] overflow-y-auto pr-1 space-y-2">
      {merged.map((m, i) => (
        <div
          key={`${m.direction}-${m.messageUid ?? m.eventUid ?? i}`}
          className={clsx(
            'rounded-lg border px-3 py-2.5 text-xs',
            m.direction === 'outbound' ? 'border-primary/30 bg-primary-soft/30' : 'border-border bg-surface',
          )}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 font-semibold text-text-primary">
              {m.direction === 'outbound' ? (
                <><Mail size={11} className="text-primary" /> To {m.toEmail}</>
              ) : (
                <><Mail size={11} className="text-text-muted" /> From {m.from}</>
              )}
            </span>
            <span className="text-2xs text-text-muted">{new Date(m.t).toLocaleString()}</span>
          </div>
          <div className="font-medium text-text-primary mb-1">{m.subject ?? '(no subject)'}</div>
          {m.direction === 'outbound' && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={clsx(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide',
                m.status === 'opened' || m.status === 'clicked' ? 'bg-success-soft text-success' :
                m.status === 'delivered' ? 'bg-info/10 text-info' :
                m.status === 'sent' ? 'bg-surface-alt text-text-secondary' :
                m.status === 'bounced' || m.status === 'failed' || m.status === 'dropped' ? 'bg-danger-soft text-danger' :
                'bg-surface-alt text-text-muted',
              )}>{m.status ?? 'queued'}</span>
              {(m.openCount ?? 0) > 0 && (
                <span className="inline-flex items-center gap-1 text-2xs text-success">
                  <MailOpen size={10} /> {m.openCount}× opened
                </span>
              )}
              {(m.clickCount ?? 0) > 0 && (
                <span className="inline-flex items-center gap-1 text-2xs text-info">
                  <MousePointerClick size={10} /> {m.clickCount}× clicked
                </span>
              )}
            </div>
          )}
          {m.direction === 'inbound' && m.snippet && (
            <div className="text-text-muted mt-1 line-clamp-3">{m.snippet}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── DealAiPanel — win probability + AI activity summary ──────────────────

function DealAiPanel({ dealUid }: { dealUid: string }) {
  const [winProb, setWinProb] = useState<WinProbabilityResult | null>(null);
  const [winLoading, setWinLoading] = useState(true);
  const [summary, setSummary] = useState<ActivitySummaryResult | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setWinLoading(true);
    dealsService
      .winProbability(dealUid)
      .then((r) => { if (!cancelled) setWinProb(r); })
      .catch(() => { if (!cancelled) setWinProb(null); })
      .finally(() => { if (!cancelled) setWinLoading(false); });
    return () => { cancelled = true; };
  }, [dealUid]);

  const runSummary = async () => {
    setSummaryLoading(true);
    try {
      const r = await dealsService.activitySummary(dealUid);
      setSummary(r);
    } catch { /* interceptor */ } finally { setSummaryLoading(false); }
  };

  const score = winProb?.score ?? 0;
  const scoreColor = score >= 70 ? 'text-success' : score >= 40 ? 'text-warning' : 'text-danger';

  return (
    <div className="rounded-xl border border-border bg-surface-alt/40 p-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Win probability</div>
          <div className={clsx('mt-1 font-display text-3xl font-bold tabular-nums', scoreColor)}>
            {winLoading ? '—' : `${score}%`}
          </div>
          {winProb && (
            <div className="mt-0.5 text-2xs text-text-muted">Stage baseline: {winProb.baseFromStage}%</div>
          )}
        </div>
        <Button variant="secondary" onClick={runSummary} disabled={summaryLoading} loading={summaryLoading}>
          AI activity summary
        </Button>
      </div>
      {winProb && winProb.factors.length > 0 && (
        <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {winProb.factors.map((f, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg bg-surface px-3 py-1.5 text-xs">
              <span className="text-text-secondary">{f.label}</span>
              <span className={clsx('font-semibold tabular-nums', f.delta > 0 ? 'text-success' : f.delta < 0 ? 'text-danger' : 'text-text-muted')}>
                {f.delta > 0 ? '+' : ''}{f.delta}
              </span>
            </div>
          ))}
        </div>
      )}
      {summary && (
        <div className="mt-4 rounded-lg border border-info/30 bg-info-soft/40 p-3">
          {summary.summary && (
            <p className="text-sm font-medium text-text-primary">{summary.summary}</p>
          )}
          {summary.bullets.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-text-secondary">
              {summary.bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-info">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Analytics strip ──────────────────────────────────────────────────────────

interface Stats {
  wonCount: number; wonValue: number;
  lostCount: number; lostValue: number;
  activeCount: number; activeValue: number; activeWeighted: number;
  winRate: number | null;
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(0)}k`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function DealsAnalyticsStrip({
  stats, currency, periodLabel, loading,
}: {
  stats: Stats; currency: string; periodLabel: string; loading: boolean;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-[88px] rounded-2xl" />)}
      </div>
    );
  }

  const cards = [
    {
      key: 'active',
      title: 'Active pipeline',
      icon: <ActivityIcon size={14} />,
      count: stats.activeCount,
      headline: formatCompact(stats.activeValue),
      sub: `weighted ${formatCompact(stats.activeWeighted)} ${currency}`,
      accent: 'text-primary',
      accentBg: 'bg-primary-soft',
    },
    {
      key: 'won',
      title: 'Won',
      icon: <TrendingUp size={14} />,
      count: stats.wonCount,
      headline: formatCompact(stats.wonValue),
      sub: `${currency} · in ${periodLabel.toLowerCase()}`,
      accent: 'text-success',
      accentBg: 'bg-success-soft',
    },
    {
      key: 'lost',
      title: 'Lost',
      icon: <TrendingDown size={14} />,
      count: stats.lostCount,
      headline: formatCompact(stats.lostValue),
      sub: `${currency} · in ${periodLabel.toLowerCase()}`,
      accent: 'text-danger',
      accentBg: 'bg-danger-soft',
    },
    {
      key: 'winrate',
      title: 'Win rate',
      icon: <Percent size={14} />,
      count: stats.wonCount + stats.lostCount,
      headline: stats.winRate === null ? '—' : `${stats.winRate.toFixed(0)}%`,
      sub: stats.winRate === null
        ? 'No closed deals yet'
        : `${stats.wonCount} of ${stats.wonCount + stats.lostCount} closed`,
      accent: 'text-info',
      accentBg: 'bg-info-soft',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.key}
          className="flex flex-col gap-2 rounded-2xl border border-border bg-surface p-4 shadow-soft"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-medium text-text-muted">
              <span className={clsx('flex h-7 w-7 items-center justify-center rounded-lg', c.accentBg, c.accent)}>
                {c.icon}
              </span>
              {c.title}
            </div>
            <span className="text-2xs font-semibold text-text-muted tabular-nums">
              {c.count.toLocaleString()}
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-display text-[22px] font-bold tracking-tight text-text-primary tabular-nums">
              {c.headline}
            </span>
          </div>
          <p className="text-2xs text-text-muted truncate">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}
